import React, { useContext, useState } from 'react';

import cx from 'classnames';
import { Button } from 'components';
import { Icon } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { ContentRendererContext } from 'lane-shared/contexts';
import { hasPermission } from 'lane-shared/helpers';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import useFlag from 'lane-shared/hooks/useFlag';
import useUserDataContext from 'lane-shared/hooks/useUserDataContext';
import { FeatureFlag } from 'constants-flags';
import { PERMISSION_KEYS } from 'constants-permissions';

import useChannelAdminContext from 'hooks/useChannelAdminContext';

import type { CustomField } from 'domains/visitorManagement/types/CustomField';

import { CustomFieldsModal } from './CustomFieldsModal';

import styles from './CustomFieldsSection.scss';

type CustomFieldsSectionProps = {
  className?: string;
  isPreview?: boolean;
};

export function CustomFieldsSection({
  className,
  isPreview = false,
}: CustomFieldsSectionProps) {
  const { t } = useTranslation();
  const contentRendererContext = useContext(ContentRendererContext);
  const userDataContext = useUserDataContext();
  const channelAdminContext = useChannelAdminContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const customFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );

  const editMode = contentRendererContext?.editMode;
  const user = userDataContext?.user;
  const channel = channelAdminContext?.channel;

  const isAdmin =
    user?.isSuperUser ||
    hasPermission(
      user?.roles || [],
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel?._id
    );

  if (!editMode || !isAdmin || !customFieldsEnabled || isPreview) {
    return null;
  }

  const customFields: CustomField[] = [];

  return (
    <>
      <div
        className={cx(styles.customFieldsSection, className)}
        data-test="customFieldsSection"
      >
        <Button
          variant="outlined"
          className={styles.addCustomFieldsButton}
          onClick={() => setIsModalOpen(true)}
          testId="addCustomFieldsButton"
          startIcon={
            <Icon
              name="plus"
              set={ICON_SET_FONTAWESOME}
              testId="addCustomFieldsIcon"
            />
          }
        >
          {t(
            'web.admin.content.features.visitor.management.general.customFields.addButton'
          )}
        </Button>
      </div>

      <CustomFieldsModal
        isOpen={isModalOpen}
        customFields={customFields}
        onClose={() => setIsModalOpen(false)}
        onAdd={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
}
